import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import cx from 'classnames';
import { getUserData } from '../../../services/API';
import { GetCharacterConfiguration } from '../../../services/CharacterService';
import { THINKING_BUBBLE, TTS_EVENTS } from '../../../constants/constants';
import { useAvatarAnchor } from './useAvatarAnchor';
import thinkingBubbleSvg from '../../../images/thinkingBubble.svg';
import './ThinkingBubble.scss';

/**
 * Request-ID-scoped state machine for the "thinking" bubble (TT-1166).
 *
 * Listens for the TTS lifecycle window events dispatched by LexaSpeech/Speech:
 *   - `ttsCallStartedEvent { detail: { requestId } }` — a TTS request went out.
 *   - `ttsRequestEndedEvent { detail: { requestId } }` — that request produced audio,
 *     ended, errored, was aborted, or was skipped.
 *
 * It maintains a Set of outstanding requestIds and returns whether the single bubble
 * should be visible. The bubble shows once the newest request has been outstanding for
 * `triggerMs` (a superseding request restarts the trigger while the bubble is not yet
 * shown), and hides once nothing is outstanding (never before `minHoldMs` of visibility,
 * to avoid a flicker).
 *
 * The machine cannot strand the bubble: a request leaves the map on the FIRST of an
 * ended event, being superseded by a newer requestId (the avatar TTS pipeline is
 * strictly one-at-a-time, so an older still-pending request is stale by definition),
 * or its per-request `failsafeMs` timer. Unmount clears every timer and entry.
 *
 * `visible` gates rendering only — timing keeps running regardless so the bubble is
 * correct the instant the avatar becomes visible again.
 *
 * @param {Object} opts
 * @param {boolean} opts.visible - Whether the avatar is on screen (gates the return).
 * @param {number} opts.triggerMs - Show only after a request is outstanding this long.
 * @param {number} opts.minHoldMs - Minimum time the bubble stays up once shown.
 * @param {number} opts.failsafeMs - Absolute per-request backstop eviction time.
 * @returns {boolean} Whether the bubble should render.
 */
function useThinkingBubble({ visible, triggerMs, minHoldMs, failsafeMs }) {
  const [showing, setShowing] = useState(false);

  // Refs so the (mount-once) event listeners always read current values. The machine is
  // purely timer-driven (no Date.now), so it behaves identically under real timers and
  // both legacy and modern Jest fake timers.
  const showingRef = useRef(false);
  const minHoldElapsedRef = useRef(false); // has the current bubble been up >= minHoldMs?
  const hidePendingRef = useRef(false);    // waiting out min-hold before hiding
  const pendingRef = useRef(new Set());    // outstanding requestIds
  const failsafeTimersRef = useRef(new Map()); // requestId -> timeoutId
  const triggerTimerRef = useRef(null);
  const minHoldTimerRef = useRef(null);

  useEffect(() => {
    const setShowingState = (next) => {
      showingRef.current = next;
      setShowing(next);
    };

    const clearTrigger = () => {
      if (triggerTimerRef.current) {
        clearTimeout(triggerTimerRef.current);
        triggerTimerRef.current = null;
      }
    };

    const clearMinHold = () => {
      if (minHoldTimerRef.current) {
        clearTimeout(minHoldTimerRef.current);
        minHoldTimerRef.current = null;
      }
    };

    const clearFailsafe = (id) => {
      const timerId = failsafeTimersRef.current.get(id);
      if (timerId) {
        clearTimeout(timerId);
        failsafeTimersRef.current.delete(id);
      }
    };

    const finishHide = () => {
      clearMinHold();
      minHoldElapsedRef.current = false;
      hidePendingRef.current = false;
      setShowingState(false);
    };

    const show = () => {
      triggerTimerRef.current = null;
      minHoldElapsedRef.current = false;
      hidePendingRef.current = false;
      setShowingState(true);
      // Start the min-hold clock; if a hide is requested before it elapses, defer to here.
      clearMinHold();
      minHoldTimerRef.current = setTimeout(() => {
        minHoldTimerRef.current = null;
        minHoldElapsedRef.current = true;
        if (hidePendingRef.current && pendingRef.current.size === 0) {
          finishHide();
        }
      }, minHoldMs);
    };

    const scheduleHideIfEmpty = () => {
      if (pendingRef.current.size > 0) {
        return;
      }
      // Nothing outstanding: cancel a not-yet-fired trigger so the bubble never appears.
      clearTrigger();
      if (!showingRef.current) {
        return;
      }
      if (minHoldElapsedRef.current) {
        finishHide();
      } else {
        // Been visible < minHoldMs; wait for the min-hold timer to hide us.
        hidePendingRef.current = true;
      }
    };

    const endRequest = (id) => {
      // Ignore malformed events (no id), matching startRequest — silently hiding on a
      // stray event would be an invisible failure.
      if (id == null) {
        return;
      }
      pendingRef.current.delete(id);
      clearFailsafe(id);
      scheduleHideIfEmpty();
    };

    const startRequest = (id) => {
      if (id == null) {
        return;
      }
      // Supersede: a newer request replaces any older pending one. The avatar TTS pipeline
      // is effectively one-at-a-time, so an older still-pending id is stale; if requests
      // ever overlapped, this degrades safely (a superseded request's later terminal event
      // is a harmless no-op, and the newest request still gates hiding).
      for (const timerId of failsafeTimersRef.current.values()) {
        clearTimeout(timerId);
      }
      failsafeTimersRef.current.clear();
      pendingRef.current.clear();

      pendingRef.current.add(id);
      // A fresh outstanding request cancels any pending hide and keeps us heading toward
      // (or staying) shown.
      hidePendingRef.current = false;
      failsafeTimersRef.current.set(id, setTimeout(() => endRequest(id), failsafeMs));

      if (!showingRef.current) {
        clearTrigger();
        triggerTimerRef.current = setTimeout(show, triggerMs);
      }
    };

    const onStart = (event) => startRequest(event && event.detail && event.detail.requestId);
    const onEnd = (event) => endRequest(event && event.detail && event.detail.requestId);

    window.addEventListener(TTS_EVENTS.CALL_STARTED, onStart);
    window.addEventListener(TTS_EVENTS.REQUEST_ENDED, onEnd);

    return () => {
      window.removeEventListener(TTS_EVENTS.CALL_STARTED, onStart);
      window.removeEventListener(TTS_EVENTS.REQUEST_ENDED, onEnd);
      clearTrigger();
      clearMinHold();
      for (const timerId of failsafeTimersRef.current.values()) {
        clearTimeout(timerId);
      }
      failsafeTimersRef.current.clear();
      pendingRef.current.clear();
    };
    // Timing config comes from frozen module constants; intentionally mount-once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return showing && visible;
}

/**
 * The "thinking" thought-bubble shown above Amira's avatar while a TTS request is slow
 * (TT-1166). Renders the standard 3-dot Loading spinner (recolored via SCSS) inside a
 * white cloud SVG, anchored to the top-right of the avatar head.
 *
 * Positioning is measured, not approximated: `useAvatarAnchor` reads the live avatar
 * element's bounding rect and projects the rig's head position into it (shared
 * avatarLayout math), so the bubble tracks Coach's actual layout across rig / OSK /
 * viewport changes without transcribing Coach's breakpoints into CSS. Purely decorative,
 * so `aria-hidden` and `pointer-events: none`.
 *
 * Rendered through a portal to `document.body` (rather than the shared `Global/Portal`,
 * which mounts under `#root`): the wrapper is `position: fixed` positioned from measured
 * viewport coordinates, so it must not sit inside any ancestor that establishes a
 * containing block. `document.body` guarantees that.
 *
 * @param {Object} props
 * @param {boolean} props.visible - Whether the avatar is visible (bubble is gated on this).
 * @param {boolean} props.webglError - True when the static image fallback is shown; anchors
 *   to the fallback PNG instead of the WebGL canvas.
 */
export default function ThinkingBubble({ visible, webglError }) {
  const showing = useThinkingBubble({
    visible,
    triggerMs: THINKING_BUBBLE.TRIGGER_MS,
    minHoldMs: THINKING_BUBBLE.MIN_HOLD_MS,
    failsafeMs: THINKING_BUBBLE.FAILSAFE_MS,
  });

  const rig = showing ? GetCharacterConfiguration(getUserData())?.rig : undefined;
  const anchor = useAvatarAnchor({ active: showing, webglError: !!webglError, rig });

  if (!showing || !anchor || typeof document === 'undefined') {
    return null;
  }

  const wrapperClasses = cx('thinkingBubble', { 'rig-new': rig === 'Amira2' });

  return ReactDOM.createPortal(
    <div
      className={wrapperClasses}
      aria-hidden="true"
      style={{ left: `${anchor.left}px`, top: `${anchor.top}px` }}
    >
      <img className="thinkingBubble-cloud" src={thinkingBubbleSvg} alt="" />
      {/* Bubble-local 3-dot indicator (not the shared Loading, whose vw/vh-sized cluster
          can't be reliably centered inside a vh-sized cloud). */}
      <div className="thinkingBubble-dots">
        <span />
        <span />
        <span />
      </div>
    </div>,
    document.body,
  );
}
